import api from './api';
import { ApiResponse, PaginatedResponse, User } from '../types';

export interface EmployeeProject {
  id: string;
  name: string;
  color?: string;
}

export interface Employee {
  id: string;
  owner_user_id: string;
  employee_user_id?: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  permissions: EmployeePermissions;
  status: 'pending' | 'active' | 'suspended';
  invitation_token?: string;
  invitation_expires?: string;
  invited_at?: string;
  accepted_at?: string;
  createdAt: string;
  updatedAt: string;
  projects?: EmployeeProject[];
  employee?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  owner?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    company_name?: string;
    avatar_url?: string;
  };
}

export interface EmployeePermissions {
  campaigns: { view: boolean; edit: boolean; delete: boolean };
  analytics: { view: boolean; export: boolean };
  content: { view: boolean; edit: boolean; delete: boolean };
  settings: { view: boolean; edit: boolean };
  integrations: { view: boolean; edit: boolean };
  billing: { view: boolean; edit: boolean };
}

export interface InvitationInfo {
  email: string;
  owner_name: string;
  company_name?: string;
  role: string;
}

interface ListFilters {
  page?: number;
  limit?: number;
  status?: 'pending' | 'active' | 'suspended';
  role?: 'admin' | 'editor' | 'viewer';
}

export const employeeService = {
  async getEmployees(filters?: ListFilters): Promise<PaginatedResponse<Employee>> {
    const response = await api.get('/employees', { params: filters });
    return response.data;
  },

  async inviteEmployee(data: {
    email: string;
    role?: 'admin' | 'editor' | 'viewer';
    permissions?: Partial<EmployeePermissions>;
    project_ids?: string[];
  }): Promise<ApiResponse<Employee>> {
    const response = await api.post('/employees/invite', data);
    return response.data;
  },

  async createEmployee(data: {
    first_name: string;
    last_name?: string;
    email: string;
    password: string;
    role?: 'admin' | 'editor' | 'viewer';
    project_ids?: string[];
  }): Promise<ApiResponse<Employee>> {
    const response = await api.post('/employees/create', data);
    return response.data;
  },

  async changeEmployeePassword(id: string, data: { password: string }): Promise<ApiResponse<void>> {
    const response = await api.patch(`/employees/${id}/password`, data);
    return response.data;
  },

  async updateEmployee(id: string, data: {
    role?: 'admin' | 'editor' | 'viewer';
    permissions?: Partial<EmployeePermissions>;
    status?: 'pending' | 'active' | 'suspended';
    project_ids?: string[];
  }): Promise<ApiResponse<Employee>> {
    const response = await api.put(`/employees/${id}`, data);
    return response.data;
  },

  async removeEmployee(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/employees/${id}`);
    return response.data;
  },

  async resendInvitation(id: string): Promise<ApiResponse<void>> {
    const response = await api.post(`/employees/${id}/resend`);
    return response.data;
  },

  // Get invitation info (public endpoint for viewing invite)
  async getInvitation(token: string): Promise<ApiResponse<InvitationInfo>> {
    const response = await api.get(`/employees/accept/${token}`);
    return response.data;
  },

  // Accept invitation (requires auth)
  async acceptInvitation(token: string): Promise<ApiResponse<Employee>> {
    const response = await api.post(`/employees/accept/${token}`);
    return response.data;
  },

  // Get teams the current user is a member of
  async getMyTeams(): Promise<ApiResponse<Employee[]>> {
    const response = await api.get('/employees/my-teams');
    return response.data;
  }
};

// Default permissions for each role
export const getDefaultPermissions = (role: 'admin' | 'editor' | 'viewer'): EmployeePermissions => {
  switch (role) {
    case 'admin':
      return {
        campaigns: { view: true, edit: true, delete: true },
        analytics: { view: true, export: true },
        content: { view: true, edit: true, delete: true },
        settings: { view: true, edit: true },
        integrations: { view: true, edit: true },
        billing: { view: true, edit: false }
      };
    case 'editor':
      return {
        campaigns: { view: true, edit: true, delete: false },
        analytics: { view: true, export: true },
        content: { view: true, edit: true, delete: false },
        settings: { view: false, edit: false },
        integrations: { view: false, edit: false },
        billing: { view: false, edit: false }
      };
    case 'viewer':
    default:
      return {
        campaigns: { view: true, edit: false, delete: false },
        analytics: { view: true, export: false },
        content: { view: true, edit: false, delete: false },
        settings: { view: false, edit: false },
        integrations: { view: false, edit: false },
        billing: { view: false, edit: false }
      };
  }
};
